<!-- @format -->

<template>
	<div
		id="cs-app"
		class="cs-app"
		:class="$route.name"
		v-if="!loading"
		v-cloak
	>
		<!-- cs-header must remain a permanent memory resident component on v-show due to csGridExport -->
		<cs-header
			v-show="routeHasHeader"
			:header-logo="headerLogo"
			:menu-expanded="menuExpanded"
			:active-component="activeComponent"
			:user="user"
			:home-url="homeUrl"
			:isRouteHasHeader="routeHasHeader"
			:root-level-help-text="rootLevelHelpText"
			:entity-level-help-text="entityLevelHelpText"
			:progress="progress"
			:has-notifications="appHasNotifications"
			@toggleSidebar="toggleSidebar"
			@toggleNotifications="toggleNotificationCenter"
			@showNotification="showNotification"
		/>
		<div class="cs-app-container">
			<cs-sidebar
				v-if="routeHasSidebar"
				v-model="siteStructure"
				:menu-expanded="menuExpanded"
				:user="user"
				:last-activity="lastActivity"
				@showNotification="showNotification"
				@toggleSidebar="toggleSidebar"
				@sidebarTransitionEnd="onSidebarTransitionEnd"
			/>

			<main
				class="cs-container"
				:class="{'cs-sidebar-expanded': menuExpanded}"
			>
				<transition name="loading">
					<router-view
						:user="user"
						:entities="siteStructure.subEntities"
						:navigation="siteStructure.navigation"
						:theme="theme"
						:header-logo="headerLogo"
						:screen="screen"
						:is-page="true"
						:in-page-component-edit-version="inPageComponentEditVersion"
						:root-level-help-text="rootLevelHelpText"
						v-if="routeHasView"
						@showNotification="showNotification"
						@setupSession="setupSession"
						@setActiveComponent="onSetActiveComponent"
						@updateTheme="updateTheme"
						@updateComponentVersion="updateComponentVersion"
					/>
				</transition>
			</main>

			<mq-layout mq="sm">
				<div
					class="cs-overlay"
					v-if="menuExpanded"
				></div>
			</mq-layout>
		</div>

		<cs-loadingbar v-if="isVisible" />

		<cs-notifications-center v-if="isVisible"></cs-notifications-center>

		<cs-systemmessages
			v-if="isVisible"
			:errors="userErrors"
			:notifications="notifications"
			:standard-page-error-message="$root.$data.config.standardPageErrorMessage"
			@dismissError="dismissError"
			@dismissNotification="dismissNotification"
			@setNotificationData="onSetNotificationData"
		/>

		<cs-system-form
			v-if="isSystemFormVisible"
			:forms-data-object="formsDataObject"
			:container-blur="true"
		/>

		<cs-system-exportform
			v-if="isSystemExportFormVisible"
			:forms-data-object="formsDataObject"
			:container-blur="true"
		/>

		<cs-system-tooltip></cs-system-tooltip>

		<cs-system-popup
			v-show="isSystemPopupVisible"
			:system-popup-options="systemPopupOptions"
			:is-system-popup-visible="isSystemPopupVisible"
			@dismissPopup="onDismissSystemPopup(...arguments)"
		/>

		<cs-navigation-popup
			v-if="isNavPopupVisible"
			:entity-url="navigationPopupEntityUrl"
			:items="navigationPopupItems"
			:target="navigationPopupTarget"
		/>

		<cs-datepicker-modal />

		<div
			class="cs-offline-routing"
			:class="{'cs-online-routing': !platformFrozen}"
			@click="onNotifiyNoInternet"
		></div>
	</div>
</template>
<script>
/** @format */

import MiTheme from '@/js/mixins/mi-theme';
import {EventComponentBus} from '@/js/events/event-componentbus.js';
import {setCSLocaleLanguage, loadDayJsObjectByLocale} from '@/js/modules/csI18n';
import $csNetworkCall from '@/js/modules/csNetworkCall.js';
import $csFileUpload from '@/js/modules/csFileUpload.js';
import {$csVeeValidate} from '@/js/modules/csValidations';
import {$subscribeToUpdates, $startConnection, $stopListeningToUpdates, $stopConnection} from '@/js/modules/cs-pushnotifications';

export default {
	name: 'cs-app',

	i18n: {},

	mixins: [MiTheme],

	components: {
		'cs-header': () => import(/* webpackChunkName: "cs-header" */ './cs-header'),
		'cs-sidebar': () => import(/* webpackChunkName: "cs-sidebar" */ './cs-sidebar'),
		'cs-loadingbar': () => import(/* webpackChunkName: "cs-loadingbar" */ './cs-loadingbar'),
		'cs-notifications-center': () => import(/* webpackChunkName: "cs-notifications-center" */ './cs-notifications-center'),
		'cs-systemmessages': () => import(/* webpackChunkName: "cs-systemmessages" */ './cs-systemmessages'),
		'cs-system-tooltip': () => import(/* webpackChunkName: "cs-system-tooltip" */ './cs-system-tooltip'),
		'cs-system-popup': () => import(/* webpackChunkName: "cs-system-popup" */ './cs-system-popup'),
		'cs-system-form': () => import(/* webpackChunkName: "cs-system-form" */ './cs-system-form'),
		'cs-system-exportform': () => import(/* webpackChunkName: "cs-system-exportform" */ './cs-system-exportform'),
		'cs-navigation-popup': () => import(/* webpackChunkName: "cs-navigation-popup" */ '@/js/components/cs-navigation/cs-navigation-popup'),
		'cs-datepicker-modal': () => import(/* webpackChunkName: "cs-datepicker-modal" */ '@/js/components/cs-datepicker-modal')
	},

	data() {
		return {
			notifications: [],
			user: {},
			headerLogo: null,
			screen: {},
			headerLogoUrl: '',
			theme: {},
			siteStructure: {
				subEntities: []
			},
			menuExpanded: false,
			lastActivity: null,
			activityChecker: null,
			timeoutNotificationInterval: null,
			loading: false,
			isSystemPopupVisible: false,
			systemPopupOptions: {},
			systemPopupPromise: null,
			systemPopupRefocusRequest: null,
			isNavPopupVisible: false,
			navigationPopupEntityUrl: null,
			navigationPopupItems: [],
			navigationPopupTarget: null,
			activeComponent: null,
			onlineConnectionActive: navigator.onLine,
			platformFrozen: false,
			internetConnectionWarning: false,
			inPageComponentEditVersion: 0,
			initialLocaleSet: false,
			displayedLocale: 'en',
			rootLevelHelpText: '',
			entityLevelHelpText: '',
			showHelpText: false,
			isDatePickerModalVisible: true,
			isSystemFormVisible: false,
			isSystemExportFormVisible: false,
			formsDataObject: {},
			progress: 0,
			appHasNotifications: false,
			activityData: {
				state: 'false',
				expiresIn: 1200,
				accessTokenExpiringNotificationTime: 60,
				sessionWarningTimeout: null,
				sessionRemaining: 1200000,
				sessionWarningTime: 60000,
				sessionTimestamp: null,
				lastRenewalTimestamp: 0,
				renewalTimeout: null,
				activityWarningResolve: null
			}
		};
	},

	created() {
		// Subscribe to the push notification hub to listen for a Config Form changes
		this.$cs_.setAppNode(this);

		$subscribeToUpdates('concurrency', '', 'EditState', 'cs-form', 'onSubscribeUpdate');

		this.headerLogoCsNetworkCallGUID = undefined;
		this.accessTokenNetworkCallGUID = undefined;

		this.$auth
			.getUserManager()
			.getUser()
			.then((user) => {
				if (user) {
					this.setupSession(user);
				}
			});
		this.sidebarTransitionActive = false;
	},

	mounted() {
		// Start connection with 'concurrency' channel for the push notification
		$startConnection('concurrency');

		EventComponentBus.$on('csUserProfileUpdated', () => {
			this.fetchUserProfile(this.user.profile.id);
		});

		EventComponentBus.$on('csShowSystemPopup', (options, resolve, callback) => {
			this.isSystemPopupVisible = true;
			this.systemPopupOptions = options;
			this.systemPopupPromise = resolve;
			this.systemPopupRefocusRequest = callback;
		});

		EventComponentBus.$on('csHideSystemPopup', () => (this.isSystemPopupVisible = false));

		EventComponentBus.$on('csToggleNavigationPopup', (event) => {
			(this.navigationPopupEntityUrl = event.entityUrl), (this.navigationPopupItems = event.navigation);
			if (this.$isMultiLingual()) {
				this.$addGroupedTranslations(this.navigationPopupItems, 'navigation', 'id', 'title', {}, false, 'subNavigation', null);
				this.navigationPopupItems = this.navigationPopupItems.map((item) => {
					item.translatedName = this.$translateWithDefaultLocalScope(`navigation.${item.id}.name`, item.name);
					return item;
				});
			}

			this.navigationPopupTarget = event.target;

			this.isNavPopupVisible = event.active;
		});

		EventComponentBus.$on('csShowNotification', (type, text, dismissible, duration, colour) => {
			this.showNotification(type, text, dismissible, duration, colour);
		});

		this.fetchHeaderLogo();

		window.addEventListener('online', this.onConnectionOnline);
		window.addEventListener('offline', this.onConnectionOffline);
		window.addEventListener('resize', this.onResizeToggleNavigationPopup);
		window.addEventListener('keydown', this.onKeyboardNavigation);
	},

	beforeDestroy() {
		this.$cs_.setAppNode(undefined);

		EventComponentBus.$off('activityPing');

		window.removeEventListener('online', this.onConnectionOnline);
		window.removeEventListener('offline', this.onConnectionOffline);
		window.removeEventListener('resize', this.onResizeToggleNavigationPopup);
		window.removeEventListener('keydown', this.onKeyboardNavigation);
	},

	destroyed() {
		// Unsubscribe from the push notifications for the Config Form
		$stopListeningToUpdates('concurrency');
		// Unsubscribe from the push notifications for the App notifications
		$stopListeningToUpdates('notifications');

		EventComponentBus.$off('csUserProfileUpdated');
		EventComponentBus.$off('csShowSystemPopup');
		EventComponentBus.$off('csHideSystemPopup');
		EventComponentBus.$off('csToggleNavigationPopup');
		EventComponentBus.$off('csShowNotification');
	},

	methods: {
		/**
		 * @public
		 * @description
		 * Debounced call to toggle the sidebar on and off
		 *
		 * @returns void
		 */
		toggleSidebar() {
			if (this.sidebarTransitionActive !== true) {
				this.sidebarActive = true;
				this.menuExpanded = !this.menuExpanded;
			}
		},

		/**
		 * @public
		 * @description
		 * Shows/hides notifications in notification center
		 */
		toggleNotificationCenter() {
			const csNotificationsCenterComponent = this.$cs_.querySelectorVue(this, {name: 'cs-notifications-center', method: 'toggleVisibility'});
			if (csNotificationsCenterComponent != undefined) csNotificationsCenterComponent.toggleVisibility();
		},

		/**
		 * @public
		 * @description
		 * captures the emit from cs-sidebar to determine when it has finished transitioning in size
		 * resets the this.sidebarTransitionActive state to allow fresh click of the toggle
		 * calls all Vue nodes with the global onResizeGlobalAppCall method to trigger reaction to the resize
		 * @returns void
		 */
		onSidebarTransitionEnd() {
			this.sidebarTransitionActive = false;
			let resizeNodes = this.$cs_.querySelectorAllVue(this, {method: 'onResizeGlobalAppCall'});
			for (let resizeNode of resizeNodes) {
				resizeNode.onResizeGlobalAppCall();
			}
		},

		/**
		 * @public
		 * @description
		 * updates the component version EMIT
		 * forces a refresh of the screen
		 */
		updateComponentVersion() {
			this.fetchScreen(true);
		},

		/**
		 * @public
		 * @description
		 * Handles sliding session management and overall expiration
		 *
		 * @param {Object} user The user object retrieved from the id server
		 */
		setupSession(user) {
			try {
				this.screen_404 = {
					id: -1,
					name: this.$translateWithDefault('pageNotFoundName'),
					components: [
						{
							componentName: 'cs-notfound',
							id: -111,
							name: this.$translateWithDefault('pageNotFound'),
							position: 0
						}
					]
				};
				const userManager = this.$auth.getUserManager();
				this.activityData.accessTokenExpiringNotificationTime = userManager.settings.accessTokenExpiringNotificationTime;

				//setup session local storage data - any new window opened will clear the activityTimeoutWarning due to activityPing call to this.dismissAllNotificationByType('activityTimeout');
				//if we have an existing session and we are in the activity timeout period a similar message must be sent to all other tabs via localStorage to clear the activityTimeout
				let previousSessionStatusId = localStorage.getItem('sessionStatusId');

				if (localStorage.getItem('activityTimeoutWarning') === 'timeout') {
					localStorage.setItem('activityTimeoutWarning', 'renew-all');
				}
				if (userManager._sessionMonitor._sid !== previousSessionStatusId) {
					localStorage.setItem('activityTimeoutWarning', 'false');
					localStorage.setItem('sessionStatusId', userManager._sessionMonitor._sid);
				}

				// handle expired session reload edge case
				if (user.expired) {
					// TODO: code for hunting down random logouts. remove later
					localStorage.setItem('lastLogoutCause', 'user expired: ' + this.$moment().format());
					this.$logoutUser();
				}

				// TODO: migrate in to login function for users and unhardcode

				// initial setup
				this.$axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.access_token;

				// handle future changes
				userManager.events.addUserLoaded((newUser) => {
					this.$axios.defaults.headers.common['Authorization'] = 'Bearer ' + newUser.access_token;
				});

				//initialize our csNetworkCall module and interceptors
				$csNetworkCall.init();

				if (!this.user.profile) {
					this.fetchUserProfile(user.profile.Id).then((auth) => {
						$subscribeToUpdates('notifications', `?jobid=${this.$getUserId()}`, 'Progress', 'cs-notifications-center', 'onProgressSubscribeUpdate', true, false, auth.access_token);
					});
				}

				$csNetworkCall.addFetchInterceptorCallback('request', (config, guid) => {
					if (config.headers['X-CS-AUTOSAVEREQUEST'] !== true) {
						EventComponentBus.$emit('activityPing', '_csNetworkcall.requestInterceptors');
					}
					if (this.onlineConnectionActive === false) {
						$csNetworkCall.rejectRequest(guid);
					}
				});
				$csNetworkCall.addFetchInterceptorCallback('response', (response, guid) => {
					if (this.onlineConnectionActive === false) {
						this.recordOfflineError(response.error);
					} else if (this.$cs_.get(response, 'status') === 401) {
						this.showNotification('danger', 'Either your session has timed out or you do not have permissions to perform this action. Please reload and try again.', true, 0);
					} else if (this.$cs_.get(response, 'status') === 403) {
						this.showNotification('danger', 'You do not have permission to perform this action. Please contact support.', true, 0);
					}
				});

				//The below need to be available should we go offline as we don't
				//want any further notifications to be sent
				const CancelToken = this.$axios.CancelToken;
				const source = CancelToken.source();

				this.$axios.interceptors.request.use((config) => {
					if (config.headers['X-CS-AUTOSAVEREQUEST'] !== true) {
						EventComponentBus.$emit('activityPing', 'cs-app.setupSession.$axios.interceptors');
					}
					config.cancelToken = source.token;
					if (this.onlineConnectionActive === false) {
						source.cancel(this.$translateWithDefault('noInternet'));
					}
					return config;
				});

				this.$axios.interceptors.response.use(
					(response) => {
						return response;
					},
					(error) => {
						if (this.$axios.isCancel(error)) {
							this.recordOfflineError(error);
							return Promise.reject(error);
						} else {
							if (error.response && error.response.status === 401) {
								this.showNotification('danger', this.$translateWithDefault('sessionTimeout'), true, 0);
							} else if (error.response && error.response.status === 403) {
								this.showNotification('danger', 'You do not have permission to perform this action. Please contact support.', true, 0);
							}
							return Promise.reject(error);
						}
					}
				);

				// initial entity and settings fetch
				this.fetchSiteStructureAndSettings();

				//Error used for testing custom FE logging in Serilog
				//throw new Error("Hello forced err");

				/**
				 * @description
				 * Activity timeout notice
				 */
				userManager.events.addAccessTokenExpiring(() => this.handleTokenExpiring());

				/**
				 * @description
				 * Access token expired user logout. should be avoided if activity takes place during the window
				 */
				userManager.events.addAccessTokenExpired(() => {
					// TODO: code for hunting down random logouts. remove later
					this.$recordError({
						type: 'warning',
						error: new Error('access token expired timeout forced logout'),
						displayToUser: false,
						errorCode: '000286'
					});
					localStorage.setItem('lastLogoutCause', 'error:000286 access token expired timeout: ' + this.$moment().format());
					this.$logoutUser();
				});

				// TODO: remove later - temporary function to record errors when silent renew doesn't work
				userManager.events.addSilentRenewError((error) => {
					// TODO: code for hunting down random logouts. remove later
					localStorage.setItem('lastSilentRenewError', error.message);
					this.$recordError({
						type: 'warning',
						error: error,
						displayToUser: false,
						errorCode: '000288'
					});
				});

				/**
				 * @description
				 * Records activity within the app from anywhere and sets the time of it to now
				 * Can be called from anywhere and is automatically called with each API request
				 */
				EventComponentBus.$on('activityPing', (caller) => {
					this.resetActivity();
					this.dismissAllNotificationByType('activityTimeout');
				});

				// initial recording
				this.lastActivity = this.$moment().subtract(30, 'seconds');
				localStorage.setItem('lastActivity', this.lastActivity.format());

				/**
				 * @description
				 * Also capture activity updates from other tabs
				 */
				window.addEventListener('storage', (event) => {
					if (event.key === 'lastActivity') {
						this.lastActivity = this.$moment(event.newValue);
					} else if (event.key === 'activityTimeoutWarning') {
						switch (event.newValue) {
							case 'end-of-session':
								this.showSessionEndWarning(false);
								break;
							case 'timeout':
								this.showActivityWarning();
								break;
							case 'clear':
								this.dismissAllNotificationByType('activityTimeout');
								break;
							case 'renew-all':
								this.activityData.state = 'renew-all';
								if (typeof this.activityData.activityWarningResolve === 'function') this.activityData.activityWarningResolve(true);
								else this.renewAccessToken();
								break;
						}
					} else if (event.key === 'lastLogoutCause') {
						if (event.newValue) {
							if (typeof this.activityData.activityWarningResolve === 'function') this.activityData.activityWarningResolve('logout-all-tabs');
							else this.$logoutUser();
						}
					}
				});

				//record the last logout error to the error logs
				if (localStorage.getItem('lastLogoutCause')) {
					let lastLogoutCause = localStorage.getItem('lastLogoutCause');
					localStorage.removeItem('lastLogoutCause');

					this.$recordError({
						type: 'warning',
						error: new Error(lastLogoutCause),
						forceRecord: true,
						displayToUser: false,
						errorCode: '000383'
					});
				}
			} catch (error) {
				this.$recordError({
					type: 'danger',
					error: error,
					displayToUser: false,
					errorCode: '000171'
				});
			}
		},

		//ACCESS TOKEN METHODS

		/**
		 * @public
		 * @description
		 * handles the token expiring logic
		 * can be called from both the OIDC event and the JS clock
		 */
		handleTokenExpiring() {
			if (this.activityData.state !== 'timeout' && this.activityData.state !== 'renewToken') {
				const lastActivity = this.$moment(localStorage.getItem('lastActivity'));
				const lastActivityFromNow = this.$moment().diff(lastActivity, 'seconds');

				let accessTokenExpiringNotificationTime = this.activityData.accessTokenExpiringNotificationTime / 60 + 0.5;

				if (lastActivityFromNow >= (this.$root.$data.config.activityThreshold - accessTokenExpiringNotificationTime) * 60) {
					//only run the warning code from here for a single tab/window. All other tabs/windows run from the storage event handler
					//calls the eventlistener for activityTimeoutWarning and calls this.showActivityWarning() on all tabs;
					localStorage.setItem('activityTimeoutWarning', 'timeout');
					this.showActivityWarning();
				} else {
					this.renewAccessToken();
				}
				this.$recordError({
					type: 'warning',
					error: new Error('access token expiring event triggered'),
					displayToUser: false,
					errorCode: '000285'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Renews the users access token
		 *
		 */
		async renewAccessToken(isCreateLog = true) {
			if (this.activityData.state !== 'renewToken' && this.activityData.state !== 'timeout') {
				this.activityData.state = 'renewToken';

				try {
					const auth = await this.$auth.getUserManager().signinSilent();
					this.activityData.expiresIn = auth.expires_in;
					this.activityData.lastRenewalTimestamp = performance.now();

					if (this.activityData.sessionTimestamp == undefined) this.setupSessionEnd(auth);

					//set a timeout for the next renewal that is always 30 seconds before the expiresIn time minus the notificationTime
					clearInterval(this.activityData.renewalTimeout);
					this.activityData.renewalTimeout = setInterval(() => {
						let monotonicTimeSinceLastRenewal = Math.floor((performance.now() - this.activityData.lastRenewalTimestamp) / 1000);

						if (performance.now() - this.activityData.sessionTimestamp > this.activityData.sessionRemaining) this.showSessionEndWarning();
						if (monotonicTimeSinceLastRenewal > this.activityData.expiresIn - this.activityData.accessTokenExpiringNotificationTime - 30) this.handleTokenExpiring(true);
					}, 1000);

					if (isCreateLog === true) this.createLog(auth);
					if (this.activityData.state !== 'false') this.activityData.state = 'false';
					if (localStorage.getItem('activityTimeoutWarning') !== 'clear') localStorage.setItem('activityTimeoutWarning', 'clear');
					else localStorage.setItem('activityTimeoutWarning', 'false');
					return auth;
				} catch (error) {
					this.$recordError({
						type: 'warning',
						error: error,
						displayToUser: true,
						errorCode: '000290'
					});
					localStorage.setItem('lastLogoutCause', 'error:000290 renewing access token: ' + this.$moment().format());
					this.$logoutUser();
				}
			}
		},

		/**
		 * @public
		 * @description
		 * sets up the end of session timers
		 * needs to be done after the first token renewal
		 * @param {Object} user - return from token renewal - contains the true auth_time
		 */
		setupSessionEnd(user) {
			let sessionLengthMilliseconds = (this.$root.$data.config.maxSessionLength - 1) * 60 * 1000;
			let authTimeMilliseconds = user.profile.auth_time * 1000;

			this.activityData.sessionWarningTime = 60000;
			this.activityData.sessionRemaining = authTimeMilliseconds - Date.now() + sessionLengthMilliseconds;

			let sessionTimeoutWarning = this.$moment().add(this.activityData.sessionRemaining, 'ms').format();
			localStorage.setItem('sessionTimeoutWarning', sessionTimeoutWarning);

			if (this.activityData.sessionRemaining <= this.activityData.sessionWarningTime) {
				this.activityData.sessionWarningTime = this.activityData.sessionRemaining;
				if (this.activityData.sessionWarningTime < 0) this.activityData.sessionWarningTime = 0;
				this.activityData.sessionRemaining = 0;
			} else {
				this.activityData.sessionRemaining = this.activityData.sessionRemaining - this.activityData.sessionWarningTime;
			}
			this.activityData.sessionTimestamp = performance.now();
		},

		/**
		 * @public
		 * @description
		 * shows the session end warning
		 * @param {Boolean} isSetLocalStorage - optional
		 */
		showSessionEndWarning(isSetLocalStorage = true) {
			if (this.activityData.sessionWarningTimeout == undefined) {
				if (isSetLocalStorage !== false) localStorage.setItem('activityTimeoutWarning', 'end-of-session');
				if (this.notifications.some((v) => v.type === 'logoutTimer') === false) this.showNotification('logoutTimer', this.$translateWithDefault('logoutTimer'), false, 0);

				this.activityData.sessionWarningTimeout = setTimeout(() => {
					localStorage.setItem('lastLogoutCause', 'final session timeout: ' + this.$moment().format());
					this.$logoutUser();
				}, this.activityData.sessionWarningTime);
			}
		},
		/**
		 * @public
		 * @description
		 * Shows activity warning in form of a system popup, disabling other interactions with the app
		 *
		 */
		async showActivityWarning() {
			if (this.activityData.state !== 'timeout') {
				this.activityData.state = 'timeout';
				const textSkeleton = this.$translateWithDefault('activityTimeoutPopup');
				let secondsLeft = 58;
				let popupOptions = {
					title: `${this.$translateWithDefault('stayLoggedIn')}?`,
					text: this.$translateWithDefault('activityTimeoutPopup'),
					buttonConfirmText: this.$translateWithDefault('stayLoggedIn'),
					buttonCancelText: this.$translateWithDefault('signOut')
				};
				popupOptions.text = textSkeleton.replace('%s%', secondsLeft);

				let resolveConfirmed = new Promise((resolve) => {
					this.activityData.activityWarningResolve = resolve;
				});

				this.timeoutNotificationInterval = setInterval(() => {
					secondsLeft--;
					if (secondsLeft <= 0) {
						clearInterval(this.timeoutNotificationInterval);
						this.activityData.activityWarningResolve('timeout');
						this.$recordError({
							type: 'warning',
							error: new Error('Did not click activity timeout banner in time, logging out.'),
							displayToUser: false,
							errorCode: '000287'
						});
					} else {
						popupOptions.text = textSkeleton.replace('%s%', secondsLeft);
					}
				}, 1000);

				let confirmed = await Promise.race([this.$showSystemPopup(popupOptions), resolveConfirmed]);
				this.handleSessionExpirationConfirmation(confirmed);
			}
		},

		/**
		 * @public
		 * @description
		 * Handles actions in system popup promise on token expiration
		 *
		 * @param {Boolean || String} confirmed param returned on resolved promise
		 */
		handleSessionExpirationConfirmation(confirmed) {
			this.activityData.activityWarningResolve = null;
			if (confirmed === true) {
				clearInterval(this.timeoutNotificationInterval);
				this.activityData.state = 'renew-all';
				localStorage.setItem('activityTimeoutWarning', 'renew-all');
				this.renewAccessToken();
			} else {
				clearInterval(this.timeoutNotificationInterval);
				if (confirmed === false) localStorage.setItem('lastLogoutCause', 'user logged out intentionally on activity notification: ' + this.$moment().format());
				else if (confirmed === 'timeout') localStorage.setItem('lastLogoutCause', 'user did not click activity notification, session timeout: ' + this.$moment().format());
				if (confirmed !== 'logout-cleanup') this.$logoutUser();
			}
			this.isSystemPopupVisible = false;
			this.systemPopupOptions = {};
			this.systemPopupPromise = null;
		},

		/**
		 * @public
		 * @description
		 * calls createLog
		 * @param {Object} auth
		 */
		async createLog(auth) {
			try {
				const userId = this.$getUserId();
				if (userId == undefined) throw new Error('User id invalid');
				let networkCallGuid = $csNetworkCall.createFetchPostRequest(`${this.$getApiBaseUrl()}/api/ActivityLog/CreateLog`, {userId: userId, token: auth.access_token}, {isBlockInterceptors: true});
				await $csNetworkCall.fetchRequest(networkCallGuid);
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: true,
					errorCode: '000381'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Reset the last activity timestamp in localStorage and data property
		 *
		 * @return void
		 */
		resetActivity() {
			this.lastActivity = this.$moment().subtract(5, 'seconds');
			localStorage.setItem('lastActivity', this.lastActivity.format());
		},

		//ACCESS TOKEN METHODS

		/**
		 * @public
		 * @description
		 * Makes an API call to get the user profile data and fetch it in the data object
		 *
		 * @param { Object } userId comes from authentication service
		 *
		 * @return void
		 */
		async fetchUserProfile(userId) {
			try {
				const dataUrl = `${this.$getApiBaseUrl()}/api/Identity/GetUser`;
				const payload = {id: userId};

				let networkCallGuid = this.$csNetworkCall.createFetchPostRequest(dataUrl, payload);
				let response = await this.$csNetworkCall.fetchRequest(networkCallGuid);

				this.$root.$data.userSettings = {
					id: response.data.id,
					accessibilityMode: response.data.accessibilityMode,
					canEditDashboard: response.data?.canEditDashboard ?? false
				};
				if (this.$isMultiLingual() && Array.isArray(response.data.locale) && response.data.locale.length > 0) {
					const parsedLocale = response.data.locale[0];
					this.switchLocaleTo(parsedLocale);
				}
				this.$set(this.user, 'profile', response.data);
				return await this.renewAccessToken();
			} catch (error) {
				this.$recordError({
					type: 'danger',
					error: error,
					displayToUser: false,
					errorCode: '000171'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Sets the logo to display in the header, either as SVG code or an image
		 * It automatically cancels a previous attempt to get the logo if we request a new one
		 * this stops us from having to handle and race conditions
		 *
		 */
		async fetchHeaderLogo() {
			if (this.headerLogoCsNetworkCallGUID !== undefined) {
				this.$csNetworkCall.cancelRequest(this.headerLogoCsNetworkCallGUID);
				this.headerLogoCsNetworkCallGUID = undefined;
			}

			if (!this.headerLogoUrl || (Array.isArray(this.headerLogoUrl) && this.headerLogoUrl.length === 0)) {
				return;
			}
			let imagePath = this.headerLogoUrl;
			if (Array.isArray(this.headerLogoUrl) && this.headerLogoUrl.length > 0) {
				if (this.headerLogoUrl[0].uniqueRef != undefined) imagePath = this.headerLogoUrl[0].uniqueRef;
				else if (this.headerLogoUrl[0].fileId != undefined) imagePath = this.headerLogoUrl[0].fileId;
				if (imagePath.indexOf('staticfiles/') !== 0) imagePath = `staticfiles/${imagePath}`;
			}
			const imageUrl = imagePath.includes('corestream-logo') ? imagePath : `${this.$getApiBaseUrl()}/${imagePath}`;
			const imageTitle = this.$root?.$data?.platformInstanceSettings?.nameOfClient ?? 'Header logo';

			const {returnedImgString, headerLogoCsNetworkCallGUID} = await this.$renderConfiguredLogo(imagePath, imageUrl, imageTitle, this.headerLogoCsNetworkCallGUID);
			this.headerLogo = returnedImgString;
			this.headerLogoCsNetworkCallGUID = headerLogoCsNetworkCallGUID;
		},

		/**
		 * @public
		 * @description
		 * Find an entity using its url and switch the currently active entity to it
		 *
		 */
		switchEntity() {
			// get matching entity and create data property to handle inactive ones
			let currentEntity = null;

			for (let i = 0, entity; (entity = this.siteStructure.subEntities[i]); i++) {
				if (this.$stringsExistAndMatchesIgnoringCase(entity.url, this.$route.params.entityorscreen)) {
					this.$set(entity, 'active', true);

					this.$setActiveEntity(entity);
					currentEntity = entity;
				} else {
					this.$set(entity, 'active', false);
				}
			}

			if (this.siteStructure.helpText !== '' && this.siteStructure.helpText != undefined) {
				this.rootLevelHelpText = this.$translateWithDefaultLocalScope('helpText', this.siteStructure.helpText);
			}
			// we have a matching entity so setup the navigation
			if (currentEntity) {
				if (Array.isArray(currentEntity.navigation)) {
					this.triggerNavigationTranslations();
				}
				if (currentEntity.headerLogoUrl && currentEntity.headerLogoUrl.length > 0) {
					this.headerLogoUrl = currentEntity.headerLogoUrl;
					this.fetchHeaderLogo();
				}

				if (currentEntity?.helpText !== '' && currentEntity?.helpText != undefined) {
					this.entityLevelHelpText = this.$translateWithDefaultLocalScope('helpText', currentEntity?.helpText ?? '');
				} else if (this.rootLevelHelpText !== '' && this.rootLevelHelpText != undefined) {
					this.entityLevelHelpText = this.rootLevelHelpText;
				}

				// and apply the theme if any
				if (currentEntity.themeTemplate) {
					this.theme = currentEntity.themeTemplate.themeSettings;
				}
			} else {
				if (this.siteStructure.themeTemplate && this.siteStructure.themeTemplate.themeSettings) {
					this.theme = this.siteStructure.themeTemplate.themeSettings;
				} else {
					this.theme = {};
				}
				this.$setActiveEntity({});
			}
			this.setupThemeCss();
		},

		/**
		 * @public
		 * @description
		 * Updates an entity data
		 */
		updateSiteStructureEntityData(data) {
			if (data.id == undefined) return;
			const entityIndex = this.siteStructure.subEntities.findIndex((ent) => ent.id === data.id);
			if (entityIndex >= 0) this.siteStructure.subEntities[entityIndex] = this.$cs_.deepClone(data);
		},

		/**
		 * @public
		 * @description
		 * Fetch all the locales configured for our application
		 *
		 * @returns Promise
		 */
		async fetchApplicationLocales() {
			try {
				const dataUrl = `${this.$getApiBaseUrl()}/api/SiteConfiguration/GetAvailableLocales`;
				const networkCallId = $csNetworkCall.createFetchGetRequest(dataUrl);
				const response = await $csNetworkCall.fetchRequest(networkCallId);
				if (response.data && response.data != undefined) {
					//temporary override till BE is up to date
					this.$root.$data.csAppLocales = response.data.map((locale) => {
						locale.name = locale.label;
						return locale;
					});
				}
				return response;
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					errorMessage: 'Error fetching app locales',
					displayToUser: true,
					errorCode: '000370'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Call BE to get an endpoint version, if we can't for whatever we will fall back to whatever we set as the default in app.js
		 *
		 * @returns {Promise}
		 */
		async getCsVersion() {
			try {
				const url = `${this.$getApiBaseUrl()}/api/GenericObjectController/GetReleaseVersion`;
				const networkCallId = $csNetworkCall.createFetchGetRequest(url);
				const response = await $csNetworkCall.fetchRequest(networkCallId);
				if (response.data && response.data != undefined) {
					this.$root.$data.csVersion = response.data;
				}
				return response;
			} catch (e) {
				return Promise.resolve();
			}
		},
		/**
		 * @public
		 * @description
		 * Get array of entities from API
		 *
		 * @returns void
		 */
		async fetchSiteStructureAndSettings() {
			this.loading = true;

			await this.fetchApplicationLocales();
			await this.getCsVersion();
			await $csVeeValidate.initializeVeeValidate();
			this.$axios
				.get(`${this.$getApiBaseUrl()}/api/SiteConfiguration/GetSiteStructure`)
				.then((response) => {
					const locales = response.data.locales;

					if (locales) {
						this.$root.$data.locales = locales;
					}

					const platformInstanceSettings = response.data.platformInstanceSettings;

					if (platformInstanceSettings) {
						if (platformInstanceSettings.regionalSettings) {
							this.$root.$data.platformInstanceSettings.dateDisplayFormat = platformInstanceSettings.regionalSettings.dateDisplayFormat;
						}

						if (platformInstanceSettings.timeDisplayFormat) {
							this.$root.$data.platformInstanceSettings.timeDisplayFormat = platformInstanceSettings.timeDisplayFormat;
						}

						if (platformInstanceSettings.idServerSettings) {
							this.$root.$data.platformInstanceSettings.passwordPolicy = platformInstanceSettings.idServerSettings.passwordPolicy;
							this.$root.$data.platformInstanceSettings.idServerSettings = platformInstanceSettings.idServerSettings;
						}

						if (platformInstanceSettings.userActionOptions) {
							this.$root.$data.platformInstanceSettings.userActionOptions = platformInstanceSettings.userActionOptions;
						}

						if (platformInstanceSettings.fileStorageSettings) {
							const fileStorageSettings = {
								fileExtensionWhitelist: platformInstanceSettings.fileStorageSettings.fileExtensionWhitelist,
								maxFileNameLength: platformInstanceSettings.fileStorageSettings.maxFileNameLength,
								maxFormPayload: parseInt(platformInstanceSettings.fileStorageSettings.maxFormPayload) || 50
							};

							this.$root.$data.platformInstanceSettings.fileStorageSettings = fileStorageSettings;
						}

						if (platformInstanceSettings.errorPageSettings) {
							this.$root.$data.platformInstanceSettings.errorPageSettings = platformInstanceSettings.errorPageSettings;
						}

						if (platformInstanceSettings.nameOfClient) {
							this.$root.$data.platformInstanceSettings.nameOfClient = platformInstanceSettings.nameOfClient;
						}

						this.$root.$data.platformInstanceSettings.accessibilityMode = platformInstanceSettings.accessibilityMode;

						this.$root.$data.clientConfigUIReadOnly = typeof platformInstanceSettings.clientConfigUIReadOnly === 'boolean' ? platformInstanceSettings.clientConfigUIReadOnly : false;

						this.$root.$data.clientConfigUIMessageForReadOnlyMode = undefined;
						if (this.$root.$data.clientConfigUIReadOnly === true) {
							if (platformInstanceSettings.clientConfigUIMessageForReadOnlyMode != undefined) {
								this.$root.$data.clientConfigUIMessageForReadOnlyMode = platformInstanceSettings.clientConfigUIMessageForReadOnlyMode;
							}
						}
					}

					$csFileUpload.setNetworkCallApplicationSettings({allowedFileExtensions: this.$getAllowedFileExtension(), maxAllowedPayload: this.$getMaxAllowedPayload()});

					this.siteStructure = response.data.siteStructure;

					if (this.$isMultiLingual()) {
						if (this.siteStructure.translations) {
							this.$transformAndAddTranslations(this.siteStructure.translations, '');
						}
					}

					this.entities = this.siteStructure.subEntities;

					if ((typeof this.siteStructure.headerLogoUrl === 'string' || Array.isArray(this.siteStructure.headerLogoUrl)) && this.siteStructure.headerLogoUrl.length > 0) {
						this.headerLogoUrl = this.siteStructure.headerLogoUrl;
					} else {
						// use the cs logo as default only if there is no logo set in the config
						this.headerLogoUrl = '/dist/images/corestream-logo-colour.svg';
					}
					this.fetchHeaderLogo();

					if (this.$route.params.entityorscreen) {
						this.switchEntity(this.$route.params.entityorscreen);
					}
					this.setupThemeCss();
					this.fetchScreen();
				})
				.catch((error) => {
					this.$recordError({
						type: 'warning',
						error: error,
						displayToUser: true,
						errorCode: '000154'
					});
				})
				.finally(() => {
					this.loading = false;

					return;
				});
		},

		/**
		 * @public
		 * @description
		 * Fetch entity page data to the screen component
		 * @param {Boolean} [isUpdateComponentVersion] optional force rerender
		 *
		 * @returns void
		 */
		fetchScreen(isUpdateComponentVersion = false) {
			try {
				let dataUrl = `${this.$getApiBaseUrl()}/api/siteconfiguration/getentityscreen`,
					activeEntity = this.$getActiveEntity(),
					fetchScreen = false;

				if (activeEntity.id) {
					fetchScreen = true;
					dataUrl += `/${activeEntity.id}`;

					if (this.$route.params.screen) {
						if (Array.isArray(activeEntity.navigation)) {
							const hasAccessToNavigation = () => activeEntity.navigation.findIndex((n) => n.url === this.$route.params.screen) >= 0;
							const hasAccessToSubNavigation = () => activeEntity.navigation.some((n) => n.subNavigation.findIndex((sn) => sn.url === this.$route.params.screen) >= 0);

							if (hasAccessToNavigation() == false && hasAccessToSubNavigation() == false) {
								// 	// If user has no permission to view the page it won't appear in the entity navigation.
								// 	// We construct an endpoint url using screen urls instead.
								// 	// We will expect 403 or 404 as the response.
								dataUrl = `${this.$getApiBaseUrl()}/api/siteconfiguration/checkentityscreen/${activeEntity.url}/${this.$route.params.screen}`;
							} else {
								for (let n = 0, navigationItem; (navigationItem = activeEntity.navigation[n]); n++) {
									if (this.$stringsExistAndMatchesIgnoringCase(navigationItem.url, this.$route.params.screen)) {
										dataUrl += `/${navigationItem.screenId}`;
									}

									if (Array.isArray(navigationItem.subNavigation)) {
										for (let s = 0, subNavigationItem; (subNavigationItem = navigationItem.subNavigation[s]); s++) {
											if (this.$stringsExistAndMatchesIgnoringCase(subNavigationItem.url, this.$route.params.screen)) {
												dataUrl += `/${subNavigationItem.screenId}`;
											}
										}
									}
								}
							}
						}
					}
				} else {
					if (this.$route.params.entityorscreen && !this.$route.params.screen) {
						// this is a root page url, call a different url
						if (Array.isArray(this.siteStructure.navigation)) {
							fetchScreen = true;

							for (let i = 0, item; (item = this.siteStructure.navigation[i]); i++) {
								if (this.$stringsExistAndMatchesIgnoringCase(item.url, this.$route.params.entityorscreen)) {
									dataUrl = `${this.$getApiBaseUrl()}/api/siteconfiguration/getrootscreen/${item.screenId}`;
								}
							}
						}
					}
				}
				if (fetchScreen) {
					if (this.fetchScreenGuid != undefined) this.$csNetworkCall.cancelRequest(this.fetchScreenGuid);
					this.fetchScreenGuid = this.$csNetworkCall.createFetchGetRequest(dataUrl);
					this.$csNetworkCall
						.fetchRequest(this.fetchScreenGuid)
						.then((response) => {
							this.fetchScreenGuid = undefined;
							if (response == undefined) return;
							this.screen = this.$recursivelyRemoveNullAndEmptyAndUndefinedFromObject(response.data);

							this.activeComponent = this.screen.components[0];

							if (this.$isMultiLingual()) {
								if (response.data.translations) {
									this.$transformAndAddTranslations(response.data.translations, '');
								} else {
									this.$i18n.mergeLocaleMessage(this.$getAppLocale(), {'standardPageErrorMessage': this.$getStandardPageErrorMessage()});
								}

								if (this.screen && this.screen.translations) {
									this.$transformAndAddTranslations(this.screen.translations, '');
								} else {
									this.$i18n.mergeLocaleMessage(this.$getAppLocale(), {'name': this.screen.name});
								}

								this.$addGroupedTranslations(this.screen.components, 'components', 'id', null, {}, false, null);
								this.screen.components = this.screen.components.map((sc) => {
									sc.translatedName = this.$translateWithDefaultLocalScope(`components.${sc.id}.name`);
									return sc;
								});

								this.triggerNavigationTranslations();
							}
							if (isUpdateComponentVersion === true) {
								this.inPageComponentEditVersion++;
							}
						})
						.catch((error) => {
							this.fetchScreenGuid = undefined;
							// When response is 404 redirect to
							if (!!this.$root.$data.activeEntity.redirectTo && !this.$route.params.screen) {
								const redirectTo = this.$root.$data.activeEntity.redirectTo;
								const redirectPath = redirectTo.startsWith('/', 0) ? redirectTo : `/${redirectTo}`;

								this.$router.replace(redirectPath);
							} else {
								if (this.onlineConnectionActive === true) {
									// this.$router.replace('/404');
									this.screen_404.components[0].status = this.$cs_.get(error, 'response', 'status');
									this.screen = this.screen_404;
									this.activeComponent = this.screen.components[0];

									if (error.response.status != 403 && error.response.status != 404) {
										this.$recordError({
											type: 'warning',
											error: error,
											displayToUser: true,
											errorCode: '000207'
										});
									}
								} else {
									this.recordOfflineError(error);
								}
							}
						});
				}
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: true,
					errorCode: '000152'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Contact the navigation component and fire the method to translate the items within it.
		 *
		 * @returns void
		 */
		triggerNavigationTranslations() {
			const navComponent = this.$cs_.querySelectorVue(this, {name: 'cs-navigation'});
			if (navComponent) {
				this.$nextTick(() => {
					navComponent.triggerTranslations();
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Display error when connection is offline
		 *
		 * @returns void
		 */
		recordOfflineError(error) {
			this.$recordError({
				type: 'warning',
				error: error,
				displayToUser: true,
				errorCode: '000260'
			});
		},

		/**
		 * @public
		 * @description
		 * Dismisses an error that is being displayed on screen
		 *
		 * @param {Boolean} dismissAll Optionally dismiss all errors at once
		 *
		 * @returns void
		 */
		dismissError(dismissAll) {
			try {
				if (dismissAll) {
					this.$root.$data.errors = [];
				} else {
					const rootDataErrorsLength = this.$root.$data.errors.length;
					for (let i = 0; i < rootDataErrorsLength; i++) {
						if (this.$root.$data.errors[i].displayToUser) {
							this.$root.$data.errors.splice(this.$root.$data.errors.indexOf(this.$root.$data.errors[i], 1));
							break;
						}
					}
				}

				if (this.$root.$data.errors.filter((e) => e.displayToUser).length == 0) {
					if (this.$root.$data.errorsCallback) this.$root.$data.errorsCallback();
				}
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: false,
					errorCode: '000088'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Remove all notifications
		 *
		 * @returns void
		 */
		removeAllNotifications() {
			for (let i = this.notifications.length - 1; i >= 0; i--) {
				this.dismissNotification(this.notifications[i].id, i);
			}
		},

		/**
		 * @public
		 * @description
		 * Dismisses a notification that is being displayed on screen
		 *
		 * @param {String} notificationId The unique id of the notification
		 * @param {Number} index [optional] index is used in preference to notificationId if it is known
		 *
		 * @returns void
		 */
		dismissNotification(notificationId, index = this.notifications.findIndex((notification) => notification.id === notificationId), isDismissAll = false) {
			try {
				//only remove notifications that have NOT been set for removal already
				if (index !== -1 && this.notifications[index].id === notificationId && this.notifications[index].prepareRemoval !== true) {
					this.notifications[index].prepareRemoval = true;
					this.notifications[index].showing = false;
					//if dismiss `No internet Connection` notification - set flag to false to allow new warning on click
					/*
					LOCALE TODO - how will we handle this? "noInternet" in locale messages
					*/
					if (this.notifications[index].text.indexOf('No internet connection') !== -1 || this.notifications[index].type == 'noInternet') this.internetConnectionWarning = false;
					if (isDismissAll === false && this.notifications[index].type === 'activityTimeout') {
						clearInterval(this.timeoutNotificationInterval);
						this.renewAccessToken();
					}
					//remove the notification from array after transition delay
					setTimeout(() => this.deleteNotification(notificationId), this.notifications[index].transitionDuration);
					this.resetActivity();
				}
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: false,
					errorCode: '000089'
				});
			}
		},

		dismissAllNotificationByType(type) {
			try {
				let isRenewAccessToken = this.notifications.some((v) => v.type === 'activityTimeout');
				for (let i = this.notifications.length - 1; i >= 0; i--) {
					let notification = this.notifications[i];
					if (notification.type === type) {
						this.dismissNotification(notification.id, i, true);
					}
				}
				if (isRenewAccessToken === true) {
					clearInterval(this.timeoutNotificationInterval);
					this.renewAccessToken();
				}
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: false,
					errorCode: '000227'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * instantly deletes a notification
		 *
		 * @param {String} notificationId The unique id of the notification
		 * @param {Number} index [optional] index is used in preference to notificationId if it is known
		 *
		 */
		deleteNotification(notificationId, index = this.notifications.findIndex((notification) => notification.id === notificationId)) {
			try {
				if (index !== -1 && this.notifications[index].id === notificationId) {
					if (this.notifications[index].timeoutId !== undefined) clearTimeout(this.notifications[index].timeoutId);
					this.notifications.splice(index, 1);
				}
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: false,
					errorCode: '000283'
				});
			}
		},
		/**
		 * @public
		 * @description
		 * Displays a notification sent from a child component
		 *
		 * @param {String} type The type of notification; 'success', 'info', etc
		 * @param {String} text The text of the notification
		 * @param {Boolean} dismissible True if the notification should have a dismiss button
		 * @param {Mixed} duration The display duration as a number of milliseconds or one if the presets 'short', 'standard', 'long'
		 *
		 * @returns void
		 */
		showNotification(type, text, dismissible, duration, colour) {
			let morphToClock = false;
			if (text.includes('morphToClock***')) {
				morphToClock = true;
				text = text.replace('morphToClock***', '');
			}
			try {
				let notification = {
					id: this.$_.uniqueId('notification_'),
					index: this.notifications.length,
					type: type,
					text: text,
					dismissible: dismissible,
					duration: duration,
					showing: true,
					morphToClock
				};

				if (notification.type === 'activityTimeout') {
					const textSkeleton = notification.text;
					let secondsLeft = 60;

					this.timeoutNotificationInterval = setInterval(() => {
						secondsLeft--;
						if (secondsLeft <= 0) {
							clearInterval(this.timeoutNotificationInterval);
							// TODO: code for hunting down random logouts. remove later
							this.$recordError({
								type: 'warning',
								error: new Error('Did not click activity timeout banner in time, logging out.'),
								displayToUser: false,
								errorCode: '000287'
							});
							localStorage.setItem('activityTimeoutWarning', 'false');
							localStorage.setItem('lastLogoutCause', 'error: 000287 user not clicked activity notice in time logout: ' + this.$moment().format());
							this.$logoutUser();
						} else {
							notification.text = textSkeleton.replace('%s%', secondsLeft);
						}
					}, 1000);

					notification.text = textSkeleton.replace('%s%', secondsLeft);
				}

				if (notification.type == 'custom' && colour) this.$set(notification, 'colour', colour);

				this.notifications.push(notification);

				return notification;
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: false,
					errorCode: '000090'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Toggles config mode
		 */
		toggleConfigMode() {
			this.$root.configModeEnabled = !this.$root.configModeEnabled;
		},

		/**
		 * @public
		 * @description
		 * sets the notification data object
		 * called by cs-systemmessages - sets timeout/duration/delay properties
		 */
		onSetNotificationData(notificationData) {
			let index;
			try {
				index = this.notifications.findIndex((notification) => notification.id === notificationData.id);
				Object.assign(this.notifications[index], notificationData);
			} catch {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: false,
					errorCode: '000284'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Sets active component object
		 *
		 * @returns void
		 */
		onSetActiveComponent(component) {
			this.activeComponent = component;
		},

		/**
		 * @public
		 * @description
		 * Resolves system popup promise
		 *
		 * @returns void
		 */
		onDismissSystemPopup(event, refocus) {
			/*if (this.platformFrozen === true) {
				this.platformFrozen = false;
				document.body.classList.remove('cs-offline-blur');
			}*/
			this.systemPopupPromise(event);

			if (this.systemPopupRefocusRequest) this.systemPopupRefocusRequest(refocus);
		},

		/**
		 * @public
		 * @description
		 * show no internet notification
		 *
		 */
		onNotifiyNoInternet() {
			if (this.internetConnectionWarning === false) {
				this.showNotification('danger', this.$translateWithDefault('noInternet'), true, 0);
				this.internetConnectionWarning = true;
			}
		},

		/**
		 * @public
		 * @description
		 * online event
		 *
		 */
		onConnectionOnline() {
			setTimeout(() => {
				if (navigator.onLine === true && this.onlineConnectionActive === false) {
					document.body.classList.remove('cs-offline-blur');
					this.onlineConnectionActive = true;
					this.dismissError(true);
					this.removeAllNotifications();

					this.$showSystemPopup({
						'title': this.$translateWithDefault('connection.timeout.title'),
						'text': this.$translateWithDefault('connection.timeout.text'),
						'enhanceBlur': true,
						'buttonConfirmText': this.$translateWithDefault('connection.timeout.buttonConfirmText'),
						'showCancelButton': false
					}).then((confirmed) => {
						if (confirmed) {
							location.reload();
						}
					});
				}
			}, 100);
		},

		/**
		 * @public
		 * @description
		 * online event
		 *
		 */
		onConnectionOffline() {
			if (this.onlineConnectionActive === true) {
				this.onlineConnectionActive = false;
				this.onNotifiyNoInternet();
				this.platformFrozen = true;
				document.body.classList.add('cs-offline-blur');
			}
		},

		/**
		 * @public
		 * @description
		 * toggles navigation popup
		 *
		 */
		onResizeToggleNavigationPopup() {
			if (this.isNavPopupVisible) {
				EventComponentBus.toggleNavigationPopup(false);
			}
		},

		/**
		 * @public
		 * @description
		 * Handles a keyboard Tab and Enter press event on the app
		 * @param { Object } event the keybard event
		 */
		onKeyboardNavigation(event) {
			// Note: I've added Enter key back because of the file download regression. If that causes other issue please, let me know. TC 08.12.2022
			if (event.key == 'Tab' || event.key == 'Enter') {
				if (this.isSystemPopupVisible) return this.$focus(event.target, event, undefined, 'popup-controls', true);
				if (this.userErrors.length > 0 || this.notifications.length > 0) return this.$focus(event.target, event, undefined, 'system-message-controls', true);

				const handler = document.activeElement.getAttribute('cs-keyboard-handler');
				if (handler != undefined) this.handleAppCustomFocus(document.activeElement, event, handler);
				else this.$focus(document.activeElement, event, document.activeElement.getAttribute('cs-keyboard'), document.activeElement.getAttribute('cs-keyboard-controls'));
			}
		},

		/**
		 * @public
		 * @description
		 * Switches a custom handler method for keyboard events to help controle non standard behaviour
		 * @param { Object } element the DOM element
		 * @param { Object } event the keyboard event
		 * @param { String } handlerComponent the name of the component to point at
		 */
		handleAppCustomFocus(element, event, handlerComponent) {
			switch (handlerComponent) {
				case 'form':
					var handlerComponentName = 'cs-form';
					break;
				case 'system-form':
					var handlerComponentName = 'cs-system-form';
					break;
				case 'report':
					var handlerComponentName = 'cs-reportsdashboard';
					break;
				case 'cs-file-upload':
					var handlerComponentName = 'cs-file-upload';
					break;
				case 'home':
					var handlerComponentName = 'cs-home';
					break;
			}

			if (handlerComponentName != undefined) {
				const component = this.$cs_.querySelectorVue(this, {name: handlerComponentName, method: 'onKeyboardHandler'});
				if (component) component.onKeyboardHandler(event, element);
			}
		},

		/**
		 * @public
		 * @description
		 * Switch the locale of the app to the new code
		 * @param {String} code - the new locale to switch to
		 * @returns void
		 */
		switchLocaleTo(code) {
			if (code == null || code == undefined) return;
			if (!this.initialLocaleSet) {
				// no need to load the popup
				this.initialLocaleSet = true;
				this.setUserLocale(code);
			} else {
				this.$showSystemPopup({
					title: this.$translateWithDefault('locale.title'),
					text: `${this.$translateWithDefault('locale.text')} '${this.$translateWithDefault(`locale.codes.${code}`)}'. ${this.$translateWithDefault('pleaseRefresh')}`,
					buttonConfirmText: this.$translateWithDefault('ok'),
					showCancelButton: true,
					forceAccessible: true
				}).then(() => {
					this.setUserLocale(code).then(() => {
						location.reload();
					});
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Interact with our csI18n module to load the new locale settings
		 * We will set our new locale and also create a localised dayjs object for use through the app
		 * @param {String} code - the new locale to switch to
		 * @returns void
		 */
		setUserLocale(code) {
			return new Promise((resolve, reject) => {
				return Promise.all([setCSLocaleLanguage(code), loadDayJsObjectByLocale(code)])
					.then(() => {
						this.displayedLocale = code;
						return resolve();
					})
					.catch((error) => {
						this.$recordError({
							type: 'error',
							error: error,
							translationArgs: [code],
							displayToUser: true,
							errorCode: '000327'
						});
						return reject(error);
					});
			});
		}
	},

	computed: {
		/**
		 * @public
		 * @description
		 * Returns whether the component is visible for the route
		 *
		 * @returns {Boolean}
		 */
		isVisible() {
			let result = false;

			if (this.$route && this.$route.name) {
				result = this.isNotAuthenticationCallback && this.isNotRoot;
			}

			return result;
		},

		/**
		 * @public
		 * @description
		 * Returns whether the route is not the root screen
		 *
		 * @returns {Boolean}
		 */
		isNotRoot() {
			return this.$route.name !== 'root';
		},

		/**
		 * @public
		 * @description
		 * Returns whether the route is not the home screen
		 *
		 * @returns {Boolean}
		 */
		isNotHome() {
			if (this.activeComponent) {
				return this.activeComponent.componentName !== 'cs-home';
			}
			return false;
		},

		/**
		 * @public
		 * @description
		 * Returns whether the route is not one of the authentication callbacks
		 *
		 * @returns {Boolean}
		 */
		isNotAuthenticationCallback() {
			return this.$route.name !== 'silentrenewcallback' && this.$route.name !== 'logincallback';
		},

		/**
		 * @public
		 * @description
		 * Returns whether the route is not the anon access workflow route
		 *
		 * @returns {Boolean}
		 */
		isNotAnonActionRoute() {
			return this.$route.name !== 'anonymousAction';
		},

		/**
		 * @public
		 * @description
		 * Returns whether the app sidebar is expanded or collapsed
		 *
		 * @returns {Boolean}
		 */
		isSidebarExpanded() {
			return this.$mq === 'xl' || this.$mq === 'xxl' ? !this.menuExpanded : this.menuExpanded;
		},

		/**
		 * @public
		 * @description
		 * Returns whether the header is required for this route
		 *
		 * @returns {Boolean}
		 */
		routeHasHeader() {
			return this.isNotAuthenticationCallback && this.user.profile != undefined && this.isNotHome && this.isNotAnonActionRoute;
		},

		/**
		 * @public
		 * @description
		 * Returns whether the sidebar is required for this route
		 *
		 * @returns {Boolean}
		 */
		routeHasSidebar() {
			return this.isNotAuthenticationCallback && this.isNotRoot && this.isNotHome && this.isNotAnonActionRoute;
		},

		/**
		 * @public
		 * @description
		 * Returns whether the user profile route is visible
		 * @returns { Boolean }
		 */
		routeHasView() {
			return this.$route.name === 'profile' ? !!this.user.profile : true;
		},

		/**
		 * @public
		 * @description
		 * Gets the user displayed errors to pass to the error handler
		 * TODO: perf_refactor_check
		 *
		 * @returns {Array}
		 */
		userErrors() {
			return this.$_.filter(this.$root.$data.errors, ['displayToUser', true]);
		},

		/**
		 * @public
		 * @description
		 * Gets the home page url
		 *
		 * @returns {String}
		 */
		homeUrl() {
			let homeUrl = null;

			if (this.siteStructure && Array.isArray(this.siteStructure.navigation)) {
				for (let i = 0, navigationItem; (navigationItem = this.siteStructure.navigation[i]); i++) {
					if (i === 0) {
						homeUrl = navigationItem.url;
					}
					if (navigationItem.isHomepage) {
						homeUrl = navigationItem.url;
					}
				}

				if (!homeUrl) {
					if (Array.isArray(this.siteStructure.subEntities) && this.siteStructure.subEntities.length > 0) {
						for (let s = 0, subEntity; (subEntity = this.siteStructure.subEntities[s]); s++) {
							if (Array.isArray(subEntity.navigation)) {
								for (let n = 0, navigationItem; (navigationItem = subEntity.navigation[n]); n++) {
									if (n === 0) {
										homeUrl = navigationItem.url;
									}
									if (navigationItem.isHomepage) {
										homeUrl = navigationItem.url;
									}
								}
							}
							if (homeUrl !== null) {
								break;
							}
						}
					} else {
						/*LOCALE TODO - are we translating URLs*/
						homeUrl = 'home';
					}
				}
			}

			return '/' + homeUrl;
		}
	},

	watch: {
		'$route.params.entityorscreen'(to, from) {
			if (to !== from) {
				this.switchEntity(to);
			}
			if (!this.$route.params.screen) {
				this.fetchScreen();
			}
		},

		'$route.params.screen'(to, from) {
			if (to !== from) {
				this.fetchScreen();
			}
		}
	}
};
</script>
<style lang="scss">
/** @format */

.cs-app {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;

	.cs-app-container {
		width: 100%;
		height: 100%;
	}

	.cs-container {
		width: 100%;
		height: 100%;
		padding-top: $cs-logo-header-height;
	}

	@include media-breakpoint-up(md) {
		.cs-container {
			padding-left: $cs-sidebar-width-shrank;

			.cs-sideoverlay:not(.cs-sideoverlay-dual-mode):not(.cs-sideoverlay-treeview-mode) {
				left: $cs-sidebar-width-shrank;
			}
		}
	}

	@include media-breakpoint-up(xl) {
		.cs-container {
			padding-left: $cs-sidebar-width;

			.cs-sideoverlay:not(.cs-sideoverlay-treeview-mode) {
				left: $cs-sidebar-width !important;
			}

			&.cs-sidebar-expanded {
				padding-left: $cs-sidebar-width-shrank;

				.cs-sideoverlay:not(.cs-sideoverlay-treeview-mode) {
					left: $cs-sidebar-width-shrank !important;
				}
			}
		}
	}
}

.cs-offline-routing {
	position: fixed;
	display: block;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	opacity: 0 !important;
	z-index: 199;
}

.cs-online-routing {
	pointer-events: none;
}

.cs-offline-blur {
	.cs-header,
	.cs-app-container {
		filter: blur(10px) !important;
		opacity: 0.5 !important;
	}
}
</style>
